import React, { FC, memo, useCallback, useMemo } from "react";
import { IconButton, Menu, Popup, ComplexOptionText, IOption } from "@evergis/ui";

import { CurrentPage, PageNavigator } from "../../styled";
import { PaginationNumber, PaginationOption } from "./styled";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { useConfigPages } from "../../../Dashboard/hooks/useConfigPages";
import { useToggle } from "../../../../hooks/useToggle";
import { usePresentationMode } from "../../../../hooks/layout/usePresentationMode";
import { usePageConfig } from "../../../Dashboard/hooks/usePageConfig";
import { WidgetType } from "../../types";

export const Pagination: FC<{ type: WidgetType }> = memo(({ type }) => {
  const { page, nextPage, prevPage, changePage } = useWidgetContext(type);
  const { isPresentationMode } = usePresentationMode();
  const [shownMenu, toggleMenu] = useToggle();
  const { pages } = useConfigPages(type);
  const { getPageTitle } = usePageConfig(type);

  const currentPage = useCallback(
    (ref?: (node: null | HTMLElement) => void) => (
      <CurrentPage ref={ref} onClick={toggleMenu}>
        {page} / {pages.length}
      </CurrentPage>
    ),
    [page, pages.length, toggleMenu],
  );

  const menuOptions = useMemo(
    () =>
      pages.map((_, index: number) => ({
        text: (
          <PaginationOption>
            <PaginationNumber>{index + 1}</PaginationNumber>
            <div>{getPageTitle(index)}</div>
          </PaginationOption>
        ),
        value: (index + 1).toString(),
      })),
    [getPageTitle, pages],
  );

  const selectPage = useCallback(
    items => {
      changePage(+items[0].value);
      toggleMenu();
    },
    [changePage, toggleMenu],
  );

  return (
    <PageNavigator>
      <IconButton kind="prev" onClick={() => prevPage(pages.length)} />
      {isPresentationMode ? (
        <Popup
          placement="bottom-start"
          isVisible={shownMenu}
          hideOnClick
          onRequestClose={toggleMenu}
          anchor={ref => currentPage(ref)}
        >
          <Menu
            options={menuOptions}
            selectOption={selectPage}
            selectedOptions={[{ value: page }] as unknown as IOption<ComplexOptionText>[]}
          />
        </Popup>
      ) : (
        currentPage()
      )}
      <IconButton kind="next" onClick={() => nextPage(pages.length)} />
    </PageNavigator>
  );
});
